import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { useFilm } from '../context/FilmContext';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, UserPlus, Mail, Trash2, Settings, Edit2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { resolveAvatarUrl } from '../components/UserAvatar';

const API = API_BASE;

// Film Roles = Job/Responsibility (informational)
const FILM_ROLES = [
  { value: 'director', label: 'Director' },
  { value: 'producer', label: 'Producer' },
  { value: 'cinematographer', label: 'Cinematographer' },
  { value: 'editor', label: 'Editor' },
  { value: 'music_director', label: 'Music Director' },
  { value: 'actor', label: 'Actor/Actress' },
  { value: 'crew', label: 'Crew' },
  { value: 'production_assistant', label: 'Production Assistant' },
  { value: 'writer', label: 'Writer' }
];

// Access Types = App Permissions
const ACCESS_TYPES = [
  { value: 'admin', label: 'Admin', description: 'Full control - manage team, edit everything' },
  { value: 'editor', label: 'Editor', description: 'Can edit film, scenes, and shots' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' }
];

const FilmManagement = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { fetchFilm, fetchFilms, films } = useFilm();
  const queryClient = useQueryClient();
  // [L] Keep the RQ-migrated Film Dashboard / Workspace Home in sync after edits
  // here (title/desc, members, cast, crew). Marks the film-scoped caches stale.
  const bustFilmCaches = React.useCallback(() => {
    if (!filmId) return;
    ['film', 'filmMembers', 'filmCast', 'filmCrew', 'filmScenes', 'filmEntitlements']
      .forEach((k) => queryClient.invalidateQueries({ queryKey: qk[k](filmId) }));
  }, [queryClient, filmId]);
  const [film, setFilm] = useState(null);
  const [members, setMembers] = useState([]);
  const [cast, setCast] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [canEditFilm, setCanEditFilm] = useState(false);
  // Honor ?tab= deep link from FilmDashboard "Manage cast/crew →" CTAs.
  // Allowed: 'details' (default), 'team', 'castcrew'.
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return ['details', 'team', 'castcrew'].includes(t) ? t : 'details';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Film details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Member management
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [memberRole, setMemberRole] = useState('crew');
  const [memberAccessType, setMemberAccessType] = useState('viewer');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('crew');
  const [inviteAccessType, setInviteAccessType] = useState('viewer');

  // Cast & Crew management
  const [castDialogOpen, setCastDialogOpen] = useState(false);
  const [crewDialogOpen, setCrewDialogOpen] = useState(false);
  const [editingCast, setEditingCast] = useState(null);
  const [editingCrew, setEditingCrew] = useState(null);
  const [castName, setCastName] = useState('');
  const [castRole, setCastRole] = useState('');
  const [castEmail, setCastEmail] = useState('');
  const [castPhone, setCastPhone] = useState('');
  const [castUserId, setCastUserId] = useState(null);
  const [castUserMatch, setCastUserMatch] = useState(null);
  const [castSearching, setCastSearching] = useState(false);
  const [crewName, setCrewName] = useState('');
  const [crewRole, setCrewRole] = useState('');
  const [crewEmail, setCrewEmail] = useState('');
  const [crewPhone, setCrewPhone] = useState('');
  const [crewUserId, setCrewUserId] = useState(null);
  const [crewUserMatch, setCrewUserMatch] = useState(null);
  const [crewSearching, setCrewSearching] = useState(false);
  useEffect(() => {
    fetchData();
  }, [filmId]);

  const fetchData = async (force = false) => {
    bustFilmCaches();
    try {
      // Always force-refresh the film itself so we get the freshest perms.
      // This avoids the "no permission to delete" bug right after a film
      // is created (when the films-list cache hasn't been updated yet).
      const [filmData, membersRes, castRes, crewRes] = await Promise.all([
        fetchFilm(filmId, true),
        axios.get(`${API}/films/${filmId}/members`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/films/${filmId}/cast`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/films/${filmId}/crew`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);

      // Kick off a films-list refresh in the background so other screens
      // (FilmSwitcher, etc.) also get fresh data — but don't block on it.
      fetchFilms(true).catch(() => {});

      setFilm(filmData);
      setTitle(filmData.title);
      setDescription(filmData.description || '');
      setMembers(membersRes.data);
      setCast(castRes.data);
      setCrew(crewRes.data);

      // Permissions (single source of truth = the film response).
      // Fall back to the films-list cache only if the new fields aren't
      // present yet (e.g., old backend, or non-FilmStats payload).
      let hasEditPerm;
      let isFilmOwner;
      if (typeof filmData?.can_edit === 'boolean' || typeof filmData?.is_owner === 'boolean') {
        hasEditPerm = !!filmData.can_edit;
        isFilmOwner = !!filmData.is_owner;
      } else {
        const currentFilmsList = (films && films.length) ? films : (await fetchFilms(true)) || [];
        const filmWithPerms = currentFilmsList.find(f => f.id === filmId);
        hasEditPerm = filmWithPerms ? filmWithPerms.can_edit : false;
        isFilmOwner = filmWithPerms ? filmWithPerms.is_owner : false;
      }

      setIsOwner(isFilmOwner);
      setCanEditFilm(hasEditPerm);

      // If user doesn't have edit permission, redirect
      if (!hasEditPerm) {
        toast.error("You don't have permission to manage this film");
        navigate(`/films/${filmId}`);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to manage this film");
        navigate(`/films/${filmId}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilmDetails = async () => {
    if (!title.trim()) {
      toast.error('Film title is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/films/${filmId}`,
        { title, description },
        { headers: getAuthHeaders() }
      );
      toast.success('Film details updated successfully');
      fetchData(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update film details';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFilm = async () => {
    const confirmMessage = `Are you sure you want to delete "${film.title}"?\n\nThis will permanently delete:\n- All scenes and shots\n- All notes and comments\n- All team members\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(`${API}/films/${filmId}`, { headers: getAuthHeaders() });
      toast.success('Film deleted successfully');
      navigate('/');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete film';
      toast.error(errorMsg);
    }
  };

  const handleSearchUsers = async (email) => {
    setSearchEmail(email);
    if (email.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    try {
      await axios.post(
        `${API}/films/${filmId}/members`,
        {
          user_id: selectedUser.id,
          role: memberRole,
          access_type: memberAccessType
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Member added successfully');
      setAddDialogOpen(false);
      setSearchEmail('');
      setSearchResults([]);
      setSelectedUser(null);
      setMemberRole('crew');
      setMemberAccessType('viewer');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add member';
      toast.error(errorMsg);
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await axios.post(
        `${API}/films/${filmId}/invitations`,
        {
          email: inviteEmail,
          role: inviteRole,
          access_type: inviteAccessType
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Invitation sent successfully');
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('crew');
      setInviteAccessType('viewer');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(errorMsg);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await axios.delete(`${API}/films/${filmId}/members/${userId}`, { headers: getAuthHeaders() });
      toast.success('Member removed successfully');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to remove member';
      toast.error(errorMsg);
    }
  };

  // Cast & Crew Handlers
  const searchUserByEmail = async (email, setMatch, setSearching) => {
    if (!email || email.length < 4 || !email.includes('@')) {
      setMatch(null);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
      if (res.data && res.data.length > 0) {
        setMatch(res.data[0]);
      } else {
        setMatch(null);
      }
    } catch {
      setMatch(null);
    } finally {
      setSearching(false);
    }
  };

  // Debounced cast email search
  const castEmailTimerRef = React.useRef(null);
  useEffect(() => () => clearTimeout(castEmailTimerRef.current), []);
  const handleCastEmailChange = (val) => {
    setCastEmail(val);
    setCastUserId(null);
    clearTimeout(castEmailTimerRef.current);
    castEmailTimerRef.current = setTimeout(() => {
      searchUserByEmail(val, setCastUserMatch, setCastSearching);
    }, 400);
  };

  const handleSelectCastUser = (match) => {
    setCastName(match.name || '');
    setCastUserId(match.id);
    setCastUserMatch(null); // hide suggestion after selection
  };

  // Debounced crew email search
  const crewEmailTimerRef = React.useRef(null);
  useEffect(() => () => clearTimeout(crewEmailTimerRef.current), []);
  const handleCrewEmailChange = (val) => {
    setCrewEmail(val);
    setCrewUserId(null);
    clearTimeout(crewEmailTimerRef.current);
    crewEmailTimerRef.current = setTimeout(() => {
      searchUserByEmail(val, setCrewUserMatch, setCrewSearching);
    }, 400);
  };

  const handleSelectCrewUser = (match) => {
    setCrewName(match.name || '');
    setCrewUserId(match.id);
    setCrewUserMatch(null);
  };

  const handleOpenAddCast = () => {
    setEditingCast(null);
    setCastName('');
    setCastRole('');
    setCastEmail('');
    setCastPhone('');
    setCastUserId(null);
    setCastUserMatch(null);
    setCastDialogOpen(true);
  };

  const handleOpenEditCast = (castMember) => {
    setEditingCast(castMember);
    setCastName(castMember.name);
    setCastRole(castMember.role || '');
    setCastEmail(castMember.email || '');
    setCastPhone(castMember.phone || '');
    setCastUserId(castMember.user_id || null);
    setCastUserMatch(null);
    setCastDialogOpen(true);
  };

  const handleSaveCast = async () => {
    if (!castName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: castName,
        role: castRole || null,
        email: castEmail || null,
        phone: castPhone || null
      };

      if (editingCast) {
        await axios.put(`${API}/films/${filmId}/cast/${editingCast.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Cast member updated');
      } else {
        await axios.post(`${API}/films/${filmId}/cast`, payload, { headers: getAuthHeaders() });
        toast.success('Cast member added');
      }
      
      setCastDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cast member');
    }
  };

  const handleDeleteCast = async (castId, castName) => {
    if (!window.confirm(`Remove ${castName} from cast?`)) return;

    try {
      await axios.delete(`${API}/films/${filmId}/cast/${castId}`, { headers: getAuthHeaders() });
      toast.success('Cast member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove cast member');
    }
  };

  const handleOpenAddCrew = () => {
    setEditingCrew(null);
    setCrewName('');
    setCrewRole('');
    setCrewEmail('');
    setCrewPhone('');
    setCrewUserId(null);
    setCrewUserMatch(null);
    setCrewDialogOpen(true);
  };

  const handleOpenEditCrew = (crewMember) => {
    setEditingCrew(crewMember);
    setCrewName(crewMember.name);
    setCrewRole(crewMember.role || '');
    setCrewEmail(crewMember.email || '');
    setCrewPhone(crewMember.phone || '');
    setCrewUserId(crewMember.user_id || null);
    setCrewUserMatch(null);
    setCrewDialogOpen(true);
  };

  const handleSaveCrew = async () => {
    if (!crewName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: crewName,
        role: crewRole || null,
        email: crewEmail || null,
        phone: crewPhone || null
      };

      if (editingCrew) {
        await axios.put(`${API}/films/${filmId}/crew/${editingCrew.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Crew member updated');
      } else {
        await axios.post(`${API}/films/${filmId}/crew`, payload, { headers: getAuthHeaders() });
        toast.success('Crew member added');
      }
      
      setCrewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save crew member');
    }
  };

  const handleDeleteCrew = async (crewId, crewName) => {
    if (!window.confirm(`Remove ${crewName} from crew?`)) return;

    try {
      await axios.delete(`${API}/films/${filmId}/crew/${crewId}`, { headers: getAuthHeaders() });
      toast.success('Crew member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove crew member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-destructive">Film not found</div>
      </div>
    );
  }

  // This check is redundant since fetchData already handles redirect,
  // but keeping it as a safety measure
  if (!canEditFilm) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">You don&apos;t have permission to manage this film</p>
          <Button onClick={() => navigate(`/films/${filmId}`)}>Back to Film</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background" data-testid="film-management-page">
      {/* Page header bar (inside AppShell) */}
      <div className="border-b border-border bg-background">
        <div className="container mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/films/${filmId}`)}
                data-testid="back-button"
                className="-ml-2 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="text-xs font-semibold tracking-wider uppercase hidden sm:inline">
                  Back
                </span>
              </Button>
              <span className="text-muted-foreground hidden md:inline">/</span>
              <span className="text-sm font-medium hidden md:inline">
                <Settings className="inline h-4 w-4 mr-1" />
                Manage
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'details'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="details-tab"
            >
              Film Details
            </button>
            <button
              onClick={() => setActiveTab('team')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'team'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="team-tab"
            >
              Team ({members.length})
            </button>
            <button
              onClick={() => setActiveTab('castcrew')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'castcrew'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="castcrew-tab"
            >
              Cast & Crew ({cast.length + crew.length})
            </button>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-4xl">
        {/* Film Details Tab */}
        {activeTab === 'details' && (
          <Card className="p-6 md:p-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold font-[Manrope] mb-4">Edit Film Details</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Update your film&apos;s title and description
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Film Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter film title"
                    data-testid="film-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter film description (optional)"
                    rows={4}
                    data-testid="film-description-input"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSaveFilmDetails}
                    disabled={saving}
                    className="flex-1 sm:flex-initial"
                    data-testid="save-film-details-button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  
                  {(isOwner || canEditFilm) && (
                    <Button
                      onClick={handleDeleteFilm}
                      variant="destructive"
                      className="flex-1 sm:flex-initial"
                      data-testid="delete-film-button"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Film
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Team Management Tab */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-member-button">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Team Member</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Search by Email</Label>
                      <Input
                        placeholder="user@example.com"
                        value={searchEmail}
                        onChange={(e) => handleSearchUsers(e.target.value)}
                        data-testid="search-email-input"
                      />
                      {searchResults.length > 0 && (
                        <div className="border rounded-sm max-h-40 overflow-y-auto">
                          {searchResults.map((usr) => (
                            <button
                              key={usr.id}
                              onClick={() => {
                                setSelectedUser(usr);
                                setSearchResults([]);
                                setSearchEmail(usr.email);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors"
                              data-testid={`search-result-${usr.id}`}
                            >
                              <div className="font-medium">{usr.name}</div>
                              <div className="text-xs text-muted-foreground">{usr.email}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {selectedUser && (
                      <>
                        <div className="space-y-2">
                          <Label>Film Role (Job/Responsibility)</Label>
                          <select
                            value={memberRole}
                            onChange={(e) => setMemberRole(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm border border-border bg-background"
                            data-testid="member-role-select"
                          >
                            {FILM_ROLES.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label>Access Type (Permissions)</Label>
                          <select
                            value={memberAccessType}
                            onChange={(e) => setMemberAccessType(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm border border-border bg-background"
                            data-testid="member-access-type-select"
                          >
                            {ACCESS_TYPES.map((accessType) => (
                              <option key={accessType.value} value={accessType.value}>
                                {accessType.label} - {accessType.description}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Determines what they can do in the app
                          </p>
                        </div>

                        <Button onClick={handleAddMember} className="w-full" data-testid="confirm-add-member">
                          Add Member
                        </Button>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="invite-button">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send Invitation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        data-testid="invite-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Film Role (Job/Responsibility)</Label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        className="w-full px-3 py-2 rounded-sm border border-border bg-background"
                        data-testid="invite-role-select"
                      >
                        {FILM_ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>Access Type (Permissions)</Label>
                      <select
                        value={inviteAccessType}
                        onChange={(e) => setInviteAccessType(e.target.value)}
                        className="w-full px-3 py-2 rounded-sm border border-border bg-background"
                        data-testid="invite-access-type-select"
                      >
                        {ACCESS_TYPES.map((accessType) => (
                          <option key={accessType.value} value={accessType.value}>
                            {accessType.label} - {accessType.description}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Determines what they can do in the app
                      </p>
                    </div>

                    <Button onClick={handleSendInvitation} className="w-full" data-testid="confirm-send-invite">
                      Send Invitation
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Members List */}
            <div className="space-y-3">
              {members.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No team members yet. Add members to collaborate!</p>
                </Card>
              ) : (
                members.map((member) => (
                  <Card key={member.id} className="p-4" data-testid={`member-card-${member.user_id}`}>
                    <div className="flex items-center gap-4">
                      {member.user_avatar_url ? (
                        <img
                          src={`${BACKEND_URL}${member.user_avatar_url}`}
                          alt={member.user_name}
                          className="h-12 w-12 rounded-full object-cover border border-border"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
                          <span className="text-sm font-semibold">
                            {member.user_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{member.user_name}</div>
                        <div className="text-sm text-muted-foreground">{member.user_email}</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 rounded-sm bg-secondary text-foreground border border-border">
                            {FILM_ROLES.find(r => r.value === member.role)?.label || member.role}
                          </span>
                          {member.access_type === 'admin' && (
                            <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/20 text-primary font-medium">
                              Admin
                            </span>
                          )}
                          {member.access_type === 'editor' && (
                            <span className="text-xs px-2 py-0.5 rounded-sm bg-success/15 text-success font-medium">
                              Editor
                            </span>
                          )}
                          {member.access_type === 'viewer' && (
                            <span className="text-xs px-2 py-0.5 rounded-sm bg-muted text-muted-foreground">
                              Viewer
                            </span>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.user_id)}
                        data-testid={`remove-member-${member.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Cast & Crew Tab */}
        {activeTab === 'castcrew' && (
          <div className="space-y-8">
            {/* Cast Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Cast</h3>
                <Button onClick={handleOpenAddCast}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Cast
                </Button>
              </div>

              {cast.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No cast members yet.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cast.map((member) => (
                    <Card key={member.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{member.name}</h4>
                            {member.user_id && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                                <CheckCircle className="h-3 w-3" /> On FrameFlow
                              </span>
                            )}
                          </div>
                          {member.role && (
                            <p className="text-sm text-muted-foreground">{member.role}</p>
                          )}
                          {member.email && (
                            <p className="text-xs text-muted-foreground mt-1">{member.email}</p>
                          )}
                          {member.phone && (
                            <p className="text-xs text-muted-foreground">{member.phone}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEditCast(member)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCast(member.id, member.name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Crew Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Crew</h3>
                <Button onClick={handleOpenAddCrew}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Crew
                </Button>
              </div>

              {crew.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">No crew members yet.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {crew.map((member) => (
                    <Card key={member.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{member.name}</h4>
                            {member.user_id && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                                <CheckCircle className="h-3 w-3" /> On FrameFlow
                              </span>
                            )}
                          </div>
                          {member.role && (
                            <p className="text-sm text-muted-foreground">{member.role}</p>
                          )}
                          {member.email && (
                            <p className="text-xs text-muted-foreground mt-1">{member.email}</p>
                          )}
                          {member.phone && (
                            <p className="text-xs text-muted-foreground">{member.phone}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEditCrew(member)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCrew(member.id, member.name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Cast Dialog */}
            <Dialog open={castDialogOpen} onOpenChange={setCastDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCast ? 'Edit Cast Member' : 'Add Cast Member'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Email field first — search for platform user */}
                  <div className="space-y-2">
                    <Label htmlFor="cast-email">Email</Label>
                    <div className="relative">
                      <Input
                        id="cast-email"
                        type="email"
                        value={castEmail}
                        onChange={(e) => handleCastEmailChange(e.target.value)}
                        placeholder="Search by email to find FrameFlow users..."
                      />
                      {castSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {/* User match suggestion */}
                    {castUserMatch && !castUserId && (
                      <button
                        type="button"
                        onClick={() => handleSelectCastUser(castUserMatch)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
                      >
                        {castUserMatch.avatar_url ? (
                          <img src={resolveAvatarUrl(castUserMatch.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                            {(castUserMatch.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{castUserMatch.name}</div>
                          <div className="text-xs text-muted-foreground">Found on FrameFlow — click to auto-fill</div>
                        </div>
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                      </button>
                    )}
                    {castUserId && (
                      <div className="flex items-center gap-2 text-xs text-primary">
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span>Linked to FrameFlow user</span>
                        <button type="button" onClick={() => { setCastUserId(null); setCastUserMatch(null); }} className="text-muted-foreground underline ml-auto">Unlink</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cast-name">Name *</Label>
                    <Input
                      id="cast-name"
                      value={castName}
                      onChange={(e) => setCastName(e.target.value)}
                      placeholder="Actor name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cast-role">Role</Label>
                    <Input
                      id="cast-role"
                      value={castRole}
                      onChange={(e) => setCastRole(e.target.value)}
                      placeholder="e.g., Lead Actor, Supporting Actor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cast-phone">Phone</Label>
                    <Input
                      id="cast-phone"
                      value={castPhone}
                      onChange={(e) => setCastPhone(e.target.value)}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setCastDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveCast}>Save</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Crew Dialog */}
            <Dialog open={crewDialogOpen} onOpenChange={setCrewDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCrew ? 'Edit Crew Member' : 'Add Crew Member'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Email field first — search for platform user */}
                  <div className="space-y-2">
                    <Label htmlFor="crew-email">Email</Label>
                    <div className="relative">
                      <Input
                        id="crew-email"
                        type="email"
                        value={crewEmail}
                        onChange={(e) => handleCrewEmailChange(e.target.value)}
                        placeholder="Search by email to find FrameFlow users..."
                      />
                      {crewSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    {/* User match suggestion */}
                    {crewUserMatch && !crewUserId && (
                      <button
                        type="button"
                        onClick={() => handleSelectCrewUser(crewUserMatch)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
                      >
                        {crewUserMatch.avatar_url ? (
                          <img src={resolveAvatarUrl(crewUserMatch.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                            {(crewUserMatch.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{crewUserMatch.name}</div>
                          <div className="text-xs text-muted-foreground">Found on FrameFlow — click to auto-fill</div>
                        </div>
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                      </button>
                    )}
                    {crewUserId && (
                      <div className="flex items-center gap-2 text-xs text-primary">
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span>Linked to FrameFlow user</span>
                        <button type="button" onClick={() => { setCrewUserId(null); setCrewUserMatch(null); }} className="text-muted-foreground underline ml-auto">Unlink</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="crew-name">Name *</Label>
                    <Input
                      id="crew-name"
                      value={crewName}
                      onChange={(e) => setCrewName(e.target.value)}
                      placeholder="Crew member name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="crew-role">Role</Label>
                    <Input
                      id="crew-role"
                      value={crewRole}
                      onChange={(e) => setCrewRole(e.target.value)}
                      placeholder="e.g., Director of Photography, Sound Engineer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="crew-phone">Phone</Label>
                    <Input
                      id="crew-phone"
                      value={crewPhone}
                      onChange={(e) => setCrewPhone(e.target.value)}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setCrewDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveCrew}>Save</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </main>
    </div>
  );
};

export default FilmManagement;
